import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';

interface FiltersSectionProps {
  allColumns: any[];
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
  fixedColumns?: string[];
  exportColumns: string[];
  courierId?: number;
  status?: 'mapped' | 'unmapped';
  onCourierChange: (courierId: number | undefined) => void;
  onStatusChange: (status: 'mapped' | 'unmapped' | undefined) => void;
}

// Hook para obtener couriers
const useCouriers = () => {
  const httpClient = createHttpClient(apiClientWH);

  return useQuery({
    queryKey: ['mappings-couriers'],
    queryFn: async () => {
      const raw = await httpClient.get<any>('/couriers');
      const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
      return list.map((courier: any) => ({
        label: courier.name,
        value: courier.id.toString(),
      }));
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const FiltersSection = ({
  allColumns,
  selectedColumns,
  onColumnsChange,
  fixedColumns = [],
  exportColumns,
  courierId,
  status,
  onCourierChange,
  onStatusChange,
}: FiltersSectionProps) => {
  const { t } = useTranslation('mappingsModule');
  const { data: couriers = [], isLoading: isLoadingCouriers } = useCouriers();

  const handleCourierChange = useCallback(
    (value: string) => {
      if (value === 'all') {
        onCourierChange(undefined);
      } else {
        onCourierChange(value ? parseInt(value, 10) : undefined);
      }
    },
    [onCourierChange],
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      if (value === 'all') {
        onStatusChange(undefined);
      } else {
        onStatusChange(value as 'mapped' | 'unmapped');
      }
    },
    [onStatusChange],
  );

  return (
    <>
      {/* 🎯 Courier y Estado en la misma fila */}
      <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-2 w-full pr-2">
        <Combobox
          options={[{ label: t('filters.all_couriers'), value: 'all' }, ...couriers]}
          value={courierId?.toString() || 'all'}
          setValue={handleCourierChange}
          dataTestId="courier-filter"
          disabled={isLoadingCouriers}
          className="min-w-0 w-full md:w-40 truncate"
          buttonClassName="whitespace-nowrap"
        />
        <Combobox
          options={[
            { label: t('filters.all_statuses'), value: 'all' },
            { label: t('filters.mapped'), value: 'mapped' },
            { label: t('filters.unmapped'), value: 'unmapped' },
          ]}
          value={status || 'all'}
          setValue={handleStatusChange}
          dataTestId="status-filter"
          className="min-w-0 w-full md:w-40 truncate"
          buttonClassName="whitespace-nowrap"
        />
        <div className="flex-shrink-0 justify-self-end">
          <ColumnSelector
            columns={allColumns}
            selected={selectedColumns}
            onColumnsChange={onColumnsChange}
            fixedColumns={fixedColumns}
            storageKey="mappings-list"
            dataTestId="mappings-column-selector"
          />
        </div>
      </div>
    </>
  );
};
